import { AUTHENTICATION_METHOD, USERNAME_FORMAT_CODE, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'
/**
 * English mirror of ./strings.ar.js — GENERATED by scripts/i18n/build.mjs.
 *
 * Wording follows the legacy Locale/*.resx English resources wherever the
 * legacy screen had the same label. To change any of it, edit
 * scripts/i18n/strings.en.json and re-run the script.
 */

export const common = {
  save: 'Save',
  saveChanges: 'Save changes',
  saveFailed: 'Could not save, please try again',
  selectFileToSave: 'Select a file before saving this section',
  notAvailable: 'This setting is not available yet',
  loadFailed: 'Could not load the settings',
  orgNamesRequired: 'The organisation name is required in Arabic and English',
  termsRequired: 'The terms of use need a text in Arabic and in English before they can be enabled',
  reset: 'Cancel',
  update: 'Update',
  active: 'Enabled',
  inactive: 'Disabled',
  show: 'Show',
  hide: 'Hide',
  yes: 'Yes',
  no: 'No',
  arabic: 'Arabic Text',
  english: 'English Text',
  status: 'Status',
  none: 'None',
  saved: 'Saved successfully',
}

export const settingsStrings = {
  title: 'Application Settings',

  theme: {
    title: 'Visual identity',
    hint: 'The primary color paints the sidebar, the learner top bar and the buttons; the selection color marks the current page in the sidebar.',
    main: 'Primary color',
    active: 'Selection color',
    mainContrast: 'Text on buttons',
    activeContrast: 'Selection on the menu',
    pass: 'Good',
    weak: 'Weak',
    restore: 'Default',
    save: 'Save',
    saved: 'Operation Completed Successfully',
  },

  /* The settings rail: nouns, grouped by what the person is doing. The page is already
     called Settings, so no entry repeats the word. */
  nav: {
    groups: {
      appearance: 'Appearance',
      content: 'Content',
      communication: 'Communication',
      access: 'Access and accounts',
      integration: 'Integration',
      system: 'System',
    },
    identity: 'Brand colours',
    media: 'Logos and images',
    keys: 'Name and footer',
    contact: 'Contact page',
    video: 'Video playback',
    userManual: 'User guides',
    documentation: 'Documentation bundle',
    notifications: 'Notifications',
    email: 'Email server',
    messaging: 'Messaging channels',
    reports: 'Reports',
    login: 'Sign-in',
    registration: 'Manual registration',
    profile: 'User profile',
    sources: 'Data sources',
    sync: 'Data sync',
    servers: 'Servers',
    services: 'Services',
    settingsKeys: 'Settings data',
    about: {
      identity: 'The colours the platform wears for its users.',
      media: 'The logos, and the images on the sign-in page.',
      keys: 'What the platform is called, and the line in its footer.',
      contact: 'What the contact page shows, and whether it shows at all.',
      video: 'How course videos play: progress, subtitles, pausing.',
      userManual: 'The guides users can open, in each language.',
      documentation: 'Import the approved guides for this version.',
      notifications: 'The wording of each notification, and how often the automatic ones go out.',
      email: 'The mail server every message leaves through.',
      messaging: 'WhatsApp, Slack and SMS — which are on, and how they connect.',
      reports: 'What reports include.',
      login: 'How people sign in: licence, authentication, one-time codes, lockout, terms.',
      registration: 'Accounts created by hand rather than by invitation.',
      profile: 'The fields a profile carries, and what they are called.',
      sources: 'Where users come from — Active Directory and LDAP.',
      sync: 'When user data is pulled from its sources.',
      servers: 'A platform spread over more than one server.',
      services: 'The services this platform talks to.',
    },
  },

  hub: {
    search: 'Search settings',
    noResults: 'No setting by that name',
    unsaved: 'Unsaved changes',
    backToList: 'Back to the list',
    discardTitle: 'Discard changes?',
    discardMessage: 'The changes in this section have not been saved.',
    discard: 'Discard',
    keepEditing: 'Keep editing',
    close: 'Close',
    readOnly: 'Read only',
    storedOnly: 'Shown as stored; these cannot be changed here yet.',
    copy: 'Copy link',
    inArabic: 'Arabic',
    inEnglish: 'English',
  },

  sheet: {
    logos: 'Logos',
    signIn: 'Sign-in page',
    platform: 'Platform',
    address: 'Address',
    connection: 'Server connection',
    sender: 'Sender',
    guideFiles: 'Guide files',
    guideDisplay: 'How the guide is shown',
    adminGuide: 'Admin panel guide',
    reportColumns: 'Profile data in reports',
    field: 'Field',
    policiesReport: 'Policies',
    examsReport: 'Exams and programmes',
    footer: 'Footer text',
    orgName: 'Organisation name',
  },

  tabs: {
    media: 'Chapter files',
    keys: 'Application Title',
    contact: 'Contact Page',
    video: 'Video Settings',
    userManual: 'User Manual',
    reports: 'Reports Settings',
    notifications: 'Notifications Settings',
    messaging: 'Messaging channels',
    email: 'Email settings',
  },

  email: {
    title: 'Email SMTP Settings',
    host: 'Host',
    port: 'Port',
    portHint: 'The port number used to connect to SMTP (usually 587 or 465).',
    from: 'Sender Email',
    fromHint: 'The address that is actually used as the SMTP Envelope (MAIL FROM).',
    senderName: 'Sender Name',
    username: 'Username',
    password: 'Password',
    ssl: 'Encryption options',
    sslOptions: {
      None: 'No encryption',
      Auto: 'Automatic',
      SslOnConnect: 'SSL on connect',
      StartTls: 'STARTTLS',
      StartTlsWhenAvailable: 'STARTTLS when available',
    },
    serviceState: 'Service state',
    skipCertCheck: 'Skip server certificate validation',
    test: 'Test Email Sending',
    testHint: 'Sent with the values in the form right now, before they are saved.',
    testTitle: 'Test email sending',
    testAddress: 'Email address for testing',
    testAddressHint: 'The test message is delivered to this address',
    testSend: 'Send'
  },

  messaging: {
    whatsapp: 'Whatsapp',
    slack: 'Slack',
    sms: 'SMS',
    unavailable: 'These channels are not available and do not send messages. Use email or in-app notifications.',
    off: 'Unavailable',
    enabled: 'Enabled',
    apiUrl: 'Api Url',
    apiKey: 'Api Key',
    senderNumber: 'Sender number',
    senderId: 'Sender id',
    webhookUrl: 'Webhook URL',
    botToken: 'Bot token',
    channel: 'Channel',
  },

  servicesUrl: {
    title: 'Services URL',
    label: 'Services URL',
  },

  media: {
    whiteLogo: 'White Logo',
    whiteLogoHint:
      'This logo is used in the admin panel, the user panel and the video page. A white logo on a transparent background works best. If you do not have a white version, upload the standard one and switch the logo background to "Show" so it is framed and renders correctly across the site.',
    whiteLogoSpec: 'PNG (590 X 345)',
    logoBackground: 'Logo background',
    mainLogo: 'Main Logo',
    mainLogoHint:
      'This logo is used on the login page, in exported reports and on the policies page, and it also appears when the user panel is scrolled down or the main menu color is changed.',
    sideImage: 'Login Side Image',
    sideImageSpec: 'PNG (792 X 1200)',
    arabicImage: 'Arabic Image',
    englishImage: 'English Image',
    loginAndLogo: 'Login page & logo',
    loginTheme: 'Login Theme',
    white: 'White',
    black: 'Black',
    logoOnLogin: 'Logo on the login page',
    background: 'Login Background Image',
    backgroundSpec: 'JPG (2048 X 1365)',
    imageAr: 'Arabic image',
    imageEn: 'English image',
    favicon: 'Browser Icon',
    faviconSpec: 'PNG (48 X 48)',
  },

  keys: {
    appTitle: 'Application Title',
    arabicAppTitle: 'Arabic Application Title',
    englishAppTitle: 'English Application Title',
    footer: 'Website Footer Text',
    footerAr: 'Website Footer Text (Arabic)',
    footerEn: 'Website Footer Text (English)',
    organization: 'Organization Data',
    arabicOrgName: 'Arabic Organization Name',
    englishOrgName: 'English Organization Name',
    orgEmail: 'Organization Email',
    orgEmailHint:
      'This email is used to alert the system administrator of a new cyber incidents. It must be associated with a user registered in the system and own system administrator privileges. you can put comma separated (,) multiple emails here. make sure the emails are linked with users.',
    domain: 'Domain URL',
    domainNote: '(Used in notifications)',
    domainSpec: 'https://www.Website-Name.com',
    useHsts: 'Enforce secure connection via HTTPS (HSTS)',
  },

  contact: {
    title: 'Contact Page',
    allDisabledWarning: 'Warning, all elements are disabled, please make sure to enable at least on element!',
    enablePage: 'Show Support Contact Info Page',
    pageTitle: 'Page Title',
    pageHeader: 'Page Header Text',
    enableEmail: 'Enable Email',
    emailAddress: 'Email Address',
    emailOptions: 'Display Email Options',
    showSendForm: 'Show send email form',
    showEmailInfo: 'Display email information',
    emailLabel: 'Email Label Text',
    showPhone: 'Show Phone Number',
    phone: 'Telephone',
    phoneLabel: 'Phone Label Text',
  },

  video: {
    title: 'Video Settings',
    progressBar: 'Enable Progress bar',
    progressBarHint:
      'Enable this option to allow users to move forward or backward within the video (seek). This is useful for long videos where viewers may want to skip or revisit certain sections.',
    forceSubtitle: 'Enable Force Subtitle',
    forceSubtitleHint:
      'Enable this option to always display subtitles when the video plays, regardless of the viewer’s settings. This is useful for accessibility or when the video has non-native language content.',
    userSubtitleControl: 'Allow users to toggle subtitles on or off during playback',
    pauseOnBlur: 'Stop video playing when window loses focus',
    pauseOnBlurHint:
      'Enable this option to automatically pause the video when the user switches to another browser tab or navigates away from the current page. This ensures the viewer doesn’t miss any part of the content.',
    playbackRates: 'Enable playback rates',
    playbackRatesHint:
      'Enable this option to let users adjust the video playback speed (e.g., slower or faster). This is useful for learning, reviewing, or skimming through content at a comfortable pace.',
  },

  userManual: {
    guide: 'User Manual',
    guideAr: 'Arabic user manual',
    guideEn: 'English user manual',
    settings: 'User Guide Settings',
    popup: 'Show user manual upon entry in a popup window',
    introVideo: 'Show a video instead of a user manual',
    linkOnLogin: 'Show the user guide link on the login page',
    separatePage: 'Show the user guide on a separate page',
    adminUrl: 'Admin Panel User Manual URL',
    published: 'Published',
    notPublished: 'Nothing published in this language yet',
    view: 'View',
    viewInPortal: 'See it as users do',
  },

  reports: {
    title: 'Reports Settings',
    userStatistics: 'Enable statistics page for user',
    policiesColumns: 'Profile Data on Policies Report',
    examsColumns: 'Profile Data on Exams Report & Programs Report',
  },

  notifications: {
    title: 'Notifications Settings',
    email: 'Email',
    index: '#',
    name: 'Name',
    lastUpdated: 'Last Updated',
    edit: 'Update',
    editTitle: 'Edit notification template',
    subject: 'Email subject',
    body: 'Email HTML content',
    bodyHint: 'You may use the placeholders already present in the template. Tenant colors and logo are applied when the message is sent.',
    preview: 'Preview',
    noPlaceholders: 'This template uses no placeholders.',
    showSamples: 'Show sample values',
    insert: 'Insert into the message',
    discardTitle: 'Discard changes?',
    missing: 'This notification template does not exist. It may have been removed.',
    discardMessage: 'The changes to this template have not been saved.',
    discard: 'Discard',
    keepEditing: 'Keep editing',
    lastUpdatedAt: 'Last updated',
    previewSubject: 'Subject',
    previewFrom: 'From',
    previewTo: 'To',
    previewHint: 'Placeholders are shown with sample values. Tenant colors and logo are applied when the message is sent.',
    placeholders: 'Placeholders in this template',
    unknownPlaceholder: 'No sample value',
    autoDays: 'Days between automatic notifications',
  },

  autoDaysTypes: [1, 2, 3, 5, 12, 13],

  templatelessTypes: [6, 7, 14],

  profileAttributes: [
    { name: 'Username', label: 'Username' },
    { name: 'Fullname', label: 'Full name' },
    { name: 'Email', label: 'Email' },
    { name: 'Department', label: 'Group' },
    { name: 'Gender', label: 'Gender' },
    { name: 'EmployeeId', label: 'Employee Id' },

    { name: 'Attribute1', setting: 'Attribute1_AR_Name' },
    { name: 'Attribute2', setting: 'Attribute2_AR_Name' },
    { name: 'Attribute3', setting: 'Attribute3_AR_Name' },
    { name: 'Attribute4', setting: 'Attribute4_AR_Name' },
    { name: 'Attribute5', setting: 'Attribute5_AR_Name' },
  ],
}

export const advancedStrings = {
  title: 'Advanced Settings',

  sheet: {
    signInMethod: 'Sign-in method',
    lockout: 'Account lockout',
    terms: 'Terms of use',
    visible: 'Shown',
    editable: 'User can edit',
    field: 'Field',
    afterSignIn: 'After sign-in',
    openProfile: 'Open the profile page after sign-in',
    customNames: 'Custom field names',
    arabic: 'العربية',
    english: 'English',
    syncSources: 'Sync sources',
    dataset: 'Data',
    source: 'Source',
    method: 'Method',
    depts: 'Departments',
    allUsers: 'All users',
    userInfo: 'Single user information',
    readProfile: 'Read profile',
    userFetch: 'User at sign-in',
    autoSync: 'Automatic sync',
    notificationService: 'Notification service',
    serviceStatus: 'Service status',
    service: 'Service',
    interval: 'Interval',
    running: 'Running',
    stopped: 'Stopped',
    sourcesEmpty: 'No data sources',
    serversEmpty: 'No additional servers',
    registrationLink: 'Registration link',
    accounts: 'Invitations and password reset',
  },

  tabs: {
    login: 'Login Settings',
    profile: 'User Profile Settings',
    sources: 'Data Sources Settings',
    sync: 'Data Sync Settings',
    registration: 'Manual Registration Settings',
    servers: 'Multi Server Settings',
    services: 'Services Settings',
  },

  login: {
    general: 'General Settings',
    generalSubtitle: 'Applies to fields on the profile page',
    licenseType: 'License Method',
    authMethod: 'Authentication Method',
    authMethodOptions: {
      [AUTHENTICATION_METHOD.LOCAL]: 'Local accounts',
      [AUTHENTICATION_METHOD.ACTIVE_DIRECTORY]: 'Active Directory',
      [AUTHENTICATION_METHOD.ACTIVE_DIRECTORY_AND_LOCAL]: 'Active Directory + Local accounts',
      [AUTHENTICATION_METHOD.PASS_LINK]: 'Passwordless magic link',
      [AUTHENTICATION_METHOD.ENTRA_ID]: 'Microsoft Entra ID',
      [AUTHENTICATION_METHOD.ENTRA_ID_AND_LOCAL]: 'Microsoft Entra ID + Local accounts',
    },
    authSource: 'Authentication Source',
    loginPage: 'Login Page',
    ldapSso: 'LDAP SSO',
    usernameFormat: 'When an email address is used as the username during the login process',
    usernameFormatOptions: [
      { value: USERNAME_FORMAT_CODE.UNCHANGED, label: 'Do Nothing' },
      {
        value: USERNAME_FORMAT_CODE.EXTRACT_USERNAME,
        label:
          'Extract the username component from the email address (for example, user123@example.com would yield user123) and use it for login',
      },
      {
        value: USERNAME_FORMAT_CODE.REJECT_EMAIL,
        label:
          'Stop the login process and inform the user to use only their username instead of their full email address.',
      },
    ],

    otp: 'One Time Password',
    otpEnabled: 'One Time Password Enabled',
    otpTarget: 'Targeted Groups',
    otpTargets: [
      { value: 'Admins', label: 'Admins Only' },
      { value: 'Users', label: 'Users Only' },
      { value: 'All', label: 'Select All' },
    ],
    otpLength: 'One Time Password Length',
    otpValidPeriod: 'One Time Password Valid Period (Seconds)',

    lockout: 'Account Lockout Settings',
    lockoutEnabled: 'Enable Account Lockout When Wrong Password Entry',
    attempts: 'Attempts Before Lockout',
    lockoutWindow: 'Account Lockout Period in Minutes',
    sessionTimeout: 'Session Timeout In Minutes',

    terms: 'Terms of Use Settings',
    termsAr: 'Arabic Terms of Use',
    termsEn: 'English Terms of Use',
    termsEnable: 'Enable Terms of Use page',
    termsEnableNote:
      '(Checking this will prevent user from using the system until he agrees upon the terms of use)',
    termsUpdated:
      'Update terms of use and force users to consent first before continuing to use the system',
    termsUpdatedNote: '(Select this option in case this is an update to the terms of use.)',
    termsSubmit: 'Save',
  },

  profile: {
    title: 'Profile Mapping',
    chooseCompany: 'Choose company',
    fieldsHeading: 'Select which field should appeare in the user profile page',
    allowEdit: 'Allow Update by User',

    fields: [
      { name: 'EnglishName', label: 'English Name' },
      { name: 'ArabicName', label: 'Arabic Name' },
      { name: 'Gender', label: 'Gender' },
      { name: 'Email', label: 'Email' },
      { name: 'Phone', label: 'Phone' },
      { name: 'Mobile', label: 'Mobile' },
      { name: 'DepartmentId', label: 'Group' },
      { name: 'EmployeeId', label: 'Employee Id' },
      { name: 'Username', label: 'Username' },

      { name: 'Attribute1', setting: 'Attribute1_AR_Name' },
      { name: 'Attribute2', setting: 'Attribute2_AR_Name' },
      { name: 'Attribute3', setting: 'Attribute3_AR_Name' },
      { name: 'Attribute4', setting: 'Attribute4_AR_Name' },
      { name: 'Attribute5', setting: 'Attribute5_AR_Name' },
    ],

    lockedEditIndex: 8,

    customAttributes: 'Custom Profile Attributes Names',
    attribute: 'Attribute',
    arabicName: 'Arabic Name:',
    englishName: 'English Name:',

    otherProperties: 'Other Properties',
    allowEditPageTitle: 'Do you want to redirect user after first loing to edit profile page?',
    allowEditPage: 'Yes, redirect to update profile page',
    forceGroupLabel: 'Force Group Change for Current User Group',
    forceGroupCheck: 'Enable force groups change for specific group if user is present in this group',
    forceGroupSelect: 'Users Groups',
  },

  sources: {
    title: 'Data Sources Settings',
    newSource: 'New Source',
    name: 'Source name',
    actions: 'Actions',
    remove: 'Delete',
    removeConfirm: 'Delete this data source? Users will no longer be able to sign in or sync through it.',
    removed: 'The data source was deleted',
    legacyType: 'legacy, unsupported',
    type: 'Type',
    arabicName: 'Arabic Name:',
    englishName: 'English Name:',
    username: 'Username',
    connectionString: 'Connection String',
    isActive: 'Active',
    edit: 'Update',
    domain: 'Directory domain (optional)',
    loginDomainHint: 'For a plain employee username, leave empty to send it unchanged. Enter a domain to send username@domain. DOMAIN\\username keeps its explicit domain. The DC path is not added to employee usernames.',
    bindUsernameHint: 'Used for browsing, sync and Test connection, independently of the employee password. Enter DOMAIN\\username or username@domain to specify its domain. For a plain account name, the configured domain is used; when empty, the DC path supplies the service account domain.',
    tenantId: 'Microsoft Entra tenant ID or verified domain',
    clientId: 'Application (client) ID',
    clientSecret: 'Client secret',
    password: 'Bind password',
    secretUnchanged: 'Leave blank to keep the stored credential.',
    graphEndpoint: 'Microsoft Graph endpoint',
    odataFilter: 'Optional Microsoft Graph user filter',
    ldapFilter: 'LDAP user search filter',
    accessGroup: 'Allowed access group ID or name (optional)',
    adminGroups: 'Rawam administrator group IDs or names (optional)',
    groupsNote: 'Use exact group names, LDAP distinguished names, or Entra group IDs. Separate groups with semicolons.',
    resignedAction: 'Resigned users action',
    resignedActionNone: 'No action',
    resignedActionHide: 'Hide them automatically',
    resignedActionAdmin: "Wait for an administrator's approval",
    resignMissing: 'Resigned when the directory no longer returns them',
    resignMissingHint: 'After a user sync, an account from this source that the directory no longer returns, and cannot find by its username either, is resigned.',
    resignInData: 'Resigned when the directory data marks them',
    resignInDataHint: 'Read on every user sync.',
    resignedVariable: 'Resigned users variable',
    resignedVariableHint: 'Format: Key=Value. Left empty, a disabled directory account counts as resigned.',
    detectUsernameChange: 'Keep the existing account when an LDAP username changes',
    detectUsernameChangeHint: 'At login, match a unique email in this directory source and preserve the account and learning history.',
    entraPrerequisites: 'Register a Web application in Microsoft Entra ID with this Rawam website /signin-entra URL as its redirect URI. Grant Microsoft Graph application permission User.Read.All with administrator consent; also grant GroupMember.Read.All when access or administrator groups are configured.',
    ssl: 'Use secure LDAP (LDAPS)',
    test: 'Test connection',
    browseUsers: 'Browse Users',
    browseGroups: 'Browse Departments',
    profileMapping: 'Profile Mapping',
    queryProperties: 'Explore Directory Properties',
    usernameToExplore: 'Username to explore properties (optional)',
    exploreButton: 'Explore Attributes',
    builtInField: 'RAWAM Field',
    sourceAttribute: 'Directory Attribute',
    notMapped: '-- Not Mapped --',
    saveMapping: 'Save Mapping',
    mappingSaved: 'Profile attributes mapping saved successfully',
    statusResigned: 'Resigned',
    statusActive: 'Active',
    noUsersFound: 'No users found in this data source',
    noGroupsFound: 'No departments found in this data source',
    discoveredAttributes: 'Discovered Attributes',
    samplePropertiesPreview: 'Sample Properties Preview',
    value: 'Value',
    resignedField: 'Resignation status',
  },

  sync: {
    legacyLdap: 'All active LDAP sources (V6 setting)',
    title: 'Data Sync Settings',
    adIntegration: 'Sync policies',
    deptsSource: 'Departments Integration Data Source',
    usersSource: 'All Users Integration Data Source',
    userFetchSource: 'User Fetch Integration Data Source',
    sourceOptions: [
      { value: 'WebAPI', label: 'WebAPI' },
      { value: 'ActiveDirectory', label: 'ActiveDirectory' },
      { value: 'None', label: 'None' },
    ],
    deptsType: 'Departments Integration Type',
    usersType: 'Users Integration Type',
    userFetchType: 'User Fetch Integration Type',
    import: 'Import',
    syncOnly: 'Sync',
    importSync: 'Import and sync',

    autoTitle: 'Auto Sync Settings',
    deptsAutoFetch: 'Departments Auto Fetch',
    usersAutoFetch: 'Users Auto Fetch',
    delay: 'Delay Before Next Run',
    overLicense: 'When Fetched Users Count Greater Than License',
    overLicenseOptions: [
      { value: 'Reject', label: 'Reject Fetch Operation' },
      { value: 'AsLicenseCount', label: 'Take As License Count' },
    ],
    deleteEmptyGroups: 'Delete Active Directory Groups With No Users',
    commandMode: 'How the synced profiles are written to the database',
    commandModeOptions: [
      { value: 'bulkSql', label: 'SQL' },
      { value: 'bulkEF', label: 'ORM' },
    ],
  },

  registration: {
    title: 'Manual Registration Settings',
    activateInvitations: 'Activate Invitations Module',
    showRegistrationLink: 'Activate the manual registration feature in the system',
    activateResetPassword: 'Activate forget password feature in the system',
    activationMethod: 'Account activation method',
    activationOptions: [
      { value: String(USER_ACTIVATION_METHOD.EMAIL), label: 'Automatic' },
      { value: String(USER_ACTIVATION_METHOD.ADMIN), label: 'Manual By Admin' },
    ],
    emailOnDomain: 'Email Should be on Domain',
    domains: 'Company Domain',
    copyLink: 'Copy Registration Link',
    copied: 'The registration link was copied',
    copyFailed: 'Could not copy — copy the link by hand',
    registerLink: 'Registration',
    noteTitle: 'Note on manual registration',
    noteBody:
      'After the user registers, an email will be sent to the user to verify the validity of the email, after which the account will be activated based on the specified activation method',
  },

  servers: {
    title: 'Multi Server Settings',
    newServer: 'New Server',
    serverId: 'Server ID',
    serverUrl: 'Server URL',
    edit: 'Update',
    actions: 'Actions',
    remove: 'Delete',
    removeConfirm: 'Delete this server?',
    removed: 'The server was deleted',
  },

  services: {
    title: 'Services Settings',
    notifications: 'Notifications',
    phishing: 'Phishing',
    sync: 'Sync',
    emailSettings: 'Email Service Settings',
    enableNotificationService: 'Enable the notifications service',
    enableNotificationServiceHint: 'Enable the service',
    maxRetries: 'Maximum Retry Attempts',
    maxRetriesHint: 'Messages are retried until they reach the maximum number of attempts.',
    maxTaskMinutes: 'Maximum Notification Sending Duration',
    maxTaskMinutesHint:
      'The longest a job may take (in minutes) to process an email. This stops two jobs working the same message at once.',
    statusHintNotifications: 'Enable the service — required by the notifications service',
    statusHint: 'Enable the service',
    delayHint: 'How long to wait before the service runs again',
    logs: 'Console Logs',
    logsHint: 'Write the service log',
    maxProcessed: 'Maximum Entries to Process',
    maxProcessedHint: 'The maximum number of jobs the service may pick up in a single run.',
    maxParallelism: 'Maximum Parallel Tasks',
    maxParallelismHint: 'The maximum number of jobs the service may run at the same time.',

    groups: [
      {
        id: 'notifications',
        items: [
          { id: 'NotificationsCreation', name: 'Notifications Creation', maxProcessed: true },
          {
            id: 'NotificationsSending',
            name: 'Notifications Sending',
            maxProcessed: true,
            maxParallelism: true,
          },
          { id: 'MarkFailedNotifications', name: 'Mark Failed Notifications' },
        ],
      },
      {
        id: 'phishing',
        items: [
          { id: 'Phishing', name: 'Phishing' },
          { id: 'EmailListener', name: 'Email Listener' },
        ],
      },
      {
        id: 'sync',
        items: [
          { id: 'SyncDepts', name: 'Departments Auto Fetch' },
          { id: 'SyncUsers', name: 'Users Auto Fetch' },
        ],
      },
    ],
  },
}

export const settingsKeysStrings = {
  reference: {
    title: 'Reference data',
    status: 'Status',
    present: 'Present',
    missing: 'Not Found',
    group: 'Group',
    id: 'Id',
    enumName: 'Code name',
    arabicName: 'Arabic name',
    englishName: 'English name',
    name: 'Name',
    editTitle: 'Edit names',
    add: 'Add',
    readOnly: 'Notification templates are edited on the notifications screen; they are listed here to check only.',
    ffmpeg: 'Video processing tool (ffmpeg)',
    groups: {
      activities: 'Activity types',
      groupTypes: 'Group types',
      difficultyLevels: 'Difficulty levels',
      courseLevels: 'Course levels',
      authMethods: 'Authentication methods',
      templates: 'Notification templates',
    },
  },
  title: 'Settings Values',
  databaseValue: 'Database Value',
  defaultValue: 'Default Value',
  none: 'None',
  key: 'Key',
  edit: 'Edit value',
  useDefault: 'Use the default',
  locked: 'A credential — changed from its own settings screen',
}

export const documentationStrings = {
  title: 'Product documentation',
  hint: 'Import the approved Rawam Center documentation bundle that matches this installation. The learner guide then appears on the user manual page.',
  installed: 'Installed guides',
  installedHint: 'What this installation has, in the language of the panel.',
  available: 'Installed',
  notImported: 'Not imported yet',
  open: 'Open',
  pdf: 'PDF',
  importTitle: 'Import a bundle',
  file: 'Signed documentation bundle',
  upload: 'Import bundle',
  busy: 'Importing…',
  success: 'Documentation imported successfully.',
  failure: 'Import failed. Check the signature, tenant identity, and release.',
  tooLarge: 'The bundle is over the 150 MB limit.',
  notConfigured: 'Documentation import is not configured on this installation — no trusted signing key or release version.',
  admin: 'Administrator guide',
  adminHint: 'For the people who run the platform.',
  user: 'User guide',
  userHint: 'For learners — shown on the user manual page.',
}
